import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from './lib/supabaseClient'
import { sendStaffInvitationEmail } from './lib/emailService'
import WhatsAppButton from './components/WhatsAppButton'
import DarkModeToggle from './components/DarkModeToggle'
import StaffManagement from './components/StaffManagement'
import MessagingSystem from './components/MessagingSystem'
import PortalLayout from './components/PortalLayout'
import { responsiveStyles } from './styles/responsiveStyles'
import NotificationBell from './components/NotificationBell'

function LeadershipPortal({ session, onLogout }) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('dashboard')
  const [staff, setStaff] = useState([])
  const [expenses, setExpenses] = useState([])
  const [salaryRequests, setSalaryRequests] = useState([])
  const [invitations, setInvitations] = useState([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('agent')
  const [inviteDepartment, setInviteDepartment] = useState('Sales')
  const [sendingInvite, setSendingInvite] = useState(false)
  const [trialInfo, setTrialInfo] = useState({ daysLeft: 0, isTrial: false })
  const [stats, setStats] = useState({
    totalAgents: 0,
    totalProperties: 0,
    totalLeads: 0,
    totalClients: 0,
    totalAppointments: 0,
    totalRevenue: { USD: 0, EUR: 0, NGN: 0 },
    pendingExpenses: 0,
    pendingSalaries: 0,
    pendingInvitations: 0
  })
  const [whatsappMessages, setWhatsappMessages] = useState([])
  const [allProperties, setAllProperties] = useState([])
  const [allLeads, setAllLeads] = useState([])
  const [allClients, setAllClients] = useState([])
  const [allAppointments, setAllAppointments] = useState([])
  const [recentActivities, setRecentActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [showPermissionModal, setShowPermissionModal] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [permissions, setPermissions] = useState({
    expenses: { view: false, create: false, approve: false },
    salaries: { view: false, approve: false }
  })
  const [showMessages, setShowMessages] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [userRole] = useState('leadership')
  const [companyId, setCompanyId] = useState(null)
  
  // Company Settings State
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    registration_number: '',
    tax_id: '',
    website: '',
    phone: '',
    address: '',
    city: '',
    country: 'Nigeria',
    logo_url: '',
    business_type: '',
    years_in_business: '',
    number_of_branches: 1,
    subscription_plan: '',
    payment_status: '',
    trial_ends_at: '',
    billing_cycle: ''
  })
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [editingCompany, setEditingCompany] = useState(false)

  // Tabs array
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'company', label: 'Company Settings', icon: '🏢' },
    { id: 'staff', label: 'Staff Management', icon: '👥' },
    { id: 'invitations', label: 'Invite Staff', icon: '📧' },
    { id: 'approvals', label: 'Pending Approvals', icon: '✅' },
    { id: 'properties', label: 'Properties', icon: '🏠' },
    { id: 'leads', label: 'Leads', icon: '🎯' },
    { id: 'clients', label: 'Clients', icon: '👤' },
    { id: 'appointments', label: 'Appointments', icon: '📅' },
    { id: 'financials', label: 'Financials', icon: '💰' },
    { id: 'whatsappLogs', label: 'WhatsApp Logs', icon: '📱' }
  ]

  const roles = [
    { value: 'agent', label: 'Agent', portal: 'Agent Portal', icon: '🤝' },
    { value: 'accounting', label: 'Accountant', portal: 'Accounting Portal', icon: '💰' },
    { value: 'marketing', label: 'Marketer', portal: 'Marketing Portal', icon: '📢' },
    { value: 'legal', label: 'Legal Officer', portal: 'Legal Portal', icon: '⚖️' },
    { value: 'recruitment', label: 'Recruiter', portal: 'Recruitment Portal', icon: '🎯' },
    { value: 'cro', label: 'Customer Relations Officer', portal: 'CRO Portal', icon: '👥' }
  ]

  const departments = ['Sales', 'Marketing', 'Accounting', 'Legal', 'Operations', 'IT', 'HR', 'Customer Relations']

  useEffect(() => {
    const getCompanyId = async () => {
      if (!session?.user?.id) {
        navigate('/login')
        return
      }

      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', session.user.id)
          .maybeSingle()

        if (error || !userData?.company_id) {
          navigate('/onboarding')
          return
        }

        setCompanyId(userData.company_id)
        setLoading(false)
      } catch (err) {
        navigate('/onboarding')
      }
    }

    getCompanyId()
  }, [session?.user?.id, navigate])

  useEffect(() => {
    if (companyId) {
      fetchAllData()
      fetchWhatsappMessages()
      fetchUnreadMessages()
      fetchInvitations()
      checkTrialStatus()
      fetchCompanyDetails()
    }
  }, [activeTab, companyId])

  const checkTrialStatus = async () => {
    if (!companyId) return
    
    try {
      const { data: company, error } = await supabase
        .from('companies')
        .select('subscription_status, trial_ends_at, payment_status, subscription_plan')
        .eq('id', companyId)
        .maybeSingle()
      
      if (error || !company) return
      
      if (company.subscription_status === 'trial' && company.payment_status !== 'completed') {
        const trialEnd = new Date(company.trial_ends_at)
        const now = new Date()
        const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24))
        setTrialInfo({ daysLeft, isTrial: true })
      }
    } catch (err) {
      console.error('Error checking trial status:', err)
    }
  }

  const fetchCompanyDetails = async () => {
    if (!companyId) return
    
    try {
      const { data: company, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .maybeSingle()
      
      if (company && !error) {
        setCompanyDetails(company)
      }
    } catch (err) {
      console.error('Error fetching company details:', err)
    }
  }

  const handleLogoUpload = async (file) => {
    if (!file || !companyId) return
    
    setUploadingLogo(true)
    
    const fileExt = file.name.split('.').pop()
    const fileName = `company-${companyId}-${Date.now()}.${fileExt}`
    const filePath = `logos/${fileName}`
    
    const { error: uploadError } = await supabase.storage
      .from('company-assets')
      .upload(filePath, file)
    
    if (uploadError) {
      alert('Upload failed: ' + uploadError.message)
      setUploadingLogo(false)
      return
    }
    
    const { data: { publicUrl } } = supabase.storage
      .from('company-assets')
      .getPublicUrl(filePath)
    
    const { error: updateError } = await supabase
      .from('companies')
      .update({ logo_url: publicUrl })
      .eq('id', companyId)
    
    if (updateError) {
      alert('Update failed: ' + updateError.message)
    } else {
      setCompanyDetails(prev => ({ ...prev, logo_url: publicUrl }))
      alert('Logo updated successfully!')
    }
    
    setUploadingLogo(false)
  }

  const updateCompanyDetails = async () => {
    if (!companyId) return
    
    setEditingCompany(true)
    
    const { error } = await supabase
      .from('companies')
      .update({
        name: companyDetails.name,
        registration_number: companyDetails.registration_number,
        tax_id: companyDetails.tax_id,
        website: companyDetails.website,
        phone: companyDetails.phone,
        address: companyDetails.address,
        city: companyDetails.city,
        country: companyDetails.country,
        business_type: companyDetails.business_type,
        number_of_branches: parseInt(companyDetails.number_of_branches) || 1
      })
      .eq('id', companyId)
    
    if (error) {
      alert('Error updating: ' + error.message)
    } else {
      alert('Company details updated successfully!')
    }
    
    setEditingCompany(false)
  }

  const fetchUnreadMessages = async () => {
    if (!session?.user?.id) return
    
    try {
      const { count } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .neq('sender_id', session.user.id)
        .eq('is_read', false)
      setUnreadCount(count || 0)
    } catch (err) {
      console.error('Error fetching unread messages:', err)
    }
  }

  const fetchInvitations = async () => {
    if (!session?.user?.id) return
    
    try {
      const { data } = await supabase
        .from('staff_invitations')
        .select('*')
        .eq('invited_by', session.user.id)
        .order('invited_at', { ascending: false })
      setInvitations(data || [])
      setStats(prev => ({
        ...prev,
        pendingInvitations: data?.filter(i => i.status === 'pending').length || 0
      }))
    } catch (err) {
      console.error('Error fetching invitations:', err)
    }
  }

  const fetchAllData = async () => {
    if (!companyId) return
    
    setLoading(true)

    try {
      const { data: usersData } = await supabase
        .from('staff_view')
        .select('*')
        .eq('company_id', companyId)
      setStaff(usersData || [])
      setStats(prev => ({ ...prev, totalAgents: usersData?.filter(u => u.role === 'agent').length || 0 }))

      const { data: propertiesData } = await supabase
        .from('properties')
        .select('*, users(email)')
        .eq('company_id', companyId)
      setAllProperties(propertiesData || [])
      setStats(prev => ({ ...prev, totalProperties: propertiesData?.length || 0 }))

      let revenue = { USD: 0, EUR: 0, NGN: 0 }
      propertiesData?.forEach(p => {
        if (p.currency === 'USD') revenue.USD += p.price || 0
        else if (p.currency === 'EUR') revenue.EUR += p.price || 0
        else if (p.currency === 'NGN') revenue.NGN += p.price || 0
      })
      setStats(prev => ({ ...prev, totalRevenue: revenue }))

      const { data: leadsData } = await supabase
        .from('leads')
        .select('*, users(email), properties(title)')
        .eq('company_id', companyId)
      setAllLeads(leadsData || [])
      setStats(prev => ({ ...prev, totalLeads: leadsData?.length || 0 }))

      const { data: clientsData } = await supabase
        .from('clients')
        .select('*, users(email)')
        .eq('company_id', companyId)
      setAllClients(clientsData || [])
      setStats(prev => ({ ...prev, totalClients: clientsData?.length || 0 }))

      const { data: appointmentsData } = await supabase
        .from('appointments')
        .select('*, users(email), clients(full_name)')
        .eq('company_id', companyId)
      setAllAppointments(appointmentsData || [])
      setStats(prev => ({ ...prev, totalAppointments: appointmentsData?.length || 0 }))

      const { data: expensesData } = await supabase
        .from('expenses')
        .select('*, users(email)')
        .eq('company_id', companyId)
        .eq('status', 'pending')
      setExpenses(expensesData || [])
      setStats(prev => ({ ...prev, pendingExpenses: expensesData?.length || 0 }))

      const { data: salaryData } = await supabase
        .from('salaries')
        .select('*, users(email)')
        .eq('company_id', companyId)
        .eq('status', 'pending')
      setSalaryRequests(salaryData || [])
      setStats(prev => ({ ...prev, pendingSalaries: salaryData?.length || 0 }))

      const recent = [
        ...(propertiesData?.slice(0, 4).map(p => ({ ...p, type: 'property', title: p.title })) || []),
        ...(leadsData?.slice(0, 3).map(l => ({ ...l, type: 'lead', title: l.client_name })) || []),
        ...(appointmentsData?.slice(0, 3).map(a => ({ ...a, type: 'appointment', title: a.clients?.full_name })) || [])
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10)
      setRecentActivities(recent)
    } catch (err) {
      console.error('Error fetching all data:', err)
    } finally {
      setLoading(false)
    }
  }

  const updateStaffRole = async (userId, newRole) => {
    await supabase.from('users').update({ role: newRole }).eq('id', userId)
    fetchAllData()
    alert(`Role updated to ${newRole}`)
  }

  const fetchWhatsappMessages = async () => {
    if (!companyId) return
    
    try {
      const { data } = await supabase
        .from('whatsapp_messages')
        .select('*')
        .order('sent_at', { ascending: false })
      setWhatsappMessages(data || [])
    } catch (err) {
      console.error('Error fetching WhatsApp messages:', err)
    }
  }

  const updatePermissions = async () => {
    if (!selectedStaff) return
    await supabase.from('users').update({ permissions }).eq('id', selectedStaff.id)
    alert('Permissions updated!')
    setShowPermissionModal(false)
    fetchAllData()
  }

  const openPermissionsModal = (staffMember) => {
    setSelectedStaff(staffMember)
    setPermissions(staffMember.permissions || { expenses: { view: false, create: false, approve: false }, salaries: { view: false, approve: false } })
    setShowPermissionModal(true)
  }

  const sendInvitation = async (e) => {
    e.preventDefault()
    if (!inviteEmail) {
      alert('Please enter an email address')
      return
    }

    setSendingInvite(true)

    try {
      const { data: company } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .maybeSingle()

      const { data: existingUser } = await supabase
        .from('users')
        .select('id, role')
        .eq('email', inviteEmail)
        .maybeSingle()

      if (existingUser) {
        if (existingUser.role) {
          alert(`User already exists with role: ${existingUser.role}. They cannot be re-invited.`)
          setSendingInvite(false)
          return
        } else {
          await supabase
            .from('users')
            .update({ role: inviteRole, department: inviteDepartment, invited_by: session.user.id, company_id: companyId })
            .eq('email', inviteEmail)
          
          alert(`${inviteEmail} has been assigned the role of ${inviteRole}.`)
          fetchAllData()
          setInviteEmail('')
          setSendingInvite(false)
          return
        }
      }

      const { data: invitation, error } = await supabase
        .from('staff_invitations')
        .insert({
          email: inviteEmail,
          role: inviteRole,
          department: inviteDepartment,
          invited_by: session.user.id,
          company_id: companyId,
          status: 'pending',
          invited_at: new Date(),
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        })
        .select()
        .single()

      if (error) throw error

      const invitationLink = `${window.location.origin}/invite/${invitation.id}`

      const emailResult = await sendStaffInvitationEmail(
        inviteEmail,
        invitationLink,
        inviteRole,
        inviteDepartment,
        session.user.email,
        company?.name || ''
      )

      if (emailResult.success) {
        alert(`✅ Invitation sent to ${inviteEmail}!\n\nThey will receive an email with a link to create their account.`)
      } else {
        console.error('Email error:', emailResult.error)
        alert(`⚠️ Invitation created but email failed to send.\n\nInvitation link: ${invitationLink}\n\nError: ${emailResult.error}`)
      }
      
      setInviteEmail('')
      setInviteRole('agent')
      setInviteDepartment('Sales')
      fetchInvitations()

    } catch (error) {
      console.error('Invitation error:', error)
      alert('Error sending invitation: ' + error.message)
    }
    setSendingInvite(false)
  }

  const resendInvitation = async (invitationId) => {
    const invitation = invitations.find(i => i.id === invitationId)
    if (!invitation) return

    const invitationLink = `${window.location.origin}/invite/${invitation.id}`
    
    const emailResult = await sendStaffInvitationEmail(
      invitation.email,
      invitationLink,
      invitation.role,
      invitation.department,
      session.user.email
    )
    
    if (emailResult.success) {
      alert(`✅ Invitation resent to ${invitation.email}!\n\nLink: ${invitationLink}`)
    } else {
      alert(`⚠️ Failed to resend email.\n\nInvitation link: ${invitationLink}\n\nError: ${emailResult.error}`)
    }
  }

  const cancelInvitation = async (invitationId) => {
    if (confirm('Cancel this invitation?')) {
      await supabase
        .from('staff_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId)
      fetchInvitations()
      alert('Invitation cancelled')
    }
  }

  const approveExpense = async (expenseId) => {
    try {
      const { error } = await supabase
        .from('expenses')
        .update({
          status: 'approved',
          approved_at: new Date(),
          approved_by: session.user.id
        })
        .eq('id', expenseId)
      if (error) throw error
      alert('Expense approved!')
      await fetchAllData()
    } catch (error) {
      alert('Error approving expense: ' + error.message)
    }
  }

  const rejectExpense = async (expenseId) => {
    const reason = prompt('Reason for rejection:')
    if (!reason) return
    try {
      const { error } = await supabase
        .from('expenses')
        .update({
          status: 'rejected',
          rejected_reason: reason,
          approved_by: session.user.id
        })
        .eq('id', expenseId)
      if (error) throw error
      alert('Expense rejected')
      await fetchAllData()
    } catch (error) {
      alert('Error rejecting expense: ' + error.message)
    }
  }

  const approveSalary = async (salaryId) => {
    try {
      const { error } = await supabase
        .from('salaries')
        .update({
          status: 'approved',
          approved_at: new Date(),
          approved_by: session.user.id
        })
        .eq('id', salaryId)
      if (error) throw error
      alert('Salary approved!')
      await fetchAllData()
    } catch (error) {
      alert('Error approving salary: ' + error.message)
    }
  }

  const getAgentStats = (agentId) => {
    return {
      properties: allProperties.filter(p => p.agent_id === agentId).length,
      leads: allLeads.filter(l => l.agent_id === agentId).length,
      clients: allClients.filter(c => c.agent_id === agentId).length,
      appointments: allAppointments.filter(a => a.agent_id === agentId).length
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      new: '#fef3c7',
      contacted: '#dbeafe',
      negotiating: '#fed7aa',
      closed: '#d1fae5',
      pending: '#fed7aa',
      completed: '#d1fae5',
      scheduled: '#dbeafe',
      approved: '#d1fae5',
      rejected: '#fee2e2'
    }
    return colors[status] || '#e2e8f0'
  }

  // Handle subscribe click - FIXED
  const handleSubscribeClick = () => {
    console.log('Subscribe clicked, navigating to pricing');
    navigate('/pricing');
  }

  // Styles
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, flexWrap: 'wrap', gap: 15 }
  const headerSubStyle = { marginBottom: 20 }
  const statCard = { background: '#fff', padding: 20, borderRadius: 20, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }
  const statNumber = { fontSize: 36, fontWeight: 700, color: '#1e3a5f', margin: '10px 0 0' }
  const revenueCard = { background: 'linear-gradient(135deg, #1e3a5f, #2c7da0)', color: '#fff', padding: 20, borderRadius: 20, marginBottom: 30 }
  const recentCard = { background: '#fff', padding: 20, borderRadius: 20, border: '1px solid #e2e8f0' }
  const recentItem = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #e2e8f0', gap: 10, flexWrap: 'wrap' }
  const cardStyle = { background: '#fff', borderRadius: 20, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }
  const tableWrapper = { overflowX: 'auto' }
  const tableStyle = { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 16, overflow: 'hidden' }
  const editBtn = { background: '#2c7da0', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }
  const deleteBtn = { background: '#dc2626', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }
  const cancelBtn = { background: '#6b7280', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
  const modalStyle = { background: '#fff', padding: 25, borderRadius: 20, width: '90%', maxWidth: 450 }
  const loadingStyle = { textAlign: 'center', padding: 50, fontSize: 18 }
  const buttonGroupStyle = { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }
  const whatsappButtonWrapper = { display: 'inline-block' }
  const inputStyle = { width: '100%', padding: '12px', margin: '8px 0', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14 }
  const addBtn = { background: '#1e3a5f', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 25, fontSize: 14, fontWeight: 600, cursor: 'pointer' }

  // Trial banner styles
  const trialBannerStyle = {
    background: trialInfo.daysLeft <= 2 ? '#fee2e2' : '#fef3c7',
    borderBottom: `2px solid ${trialInfo.daysLeft <= 2 ? '#dc2626' : '#f59e0b'}`,
    padding: '12px 20px',
    marginBottom: '20px',
    borderRadius: '8px'
  }

  const trialBannerContent = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    maxWidth: '1200px',
    margin: '0 auto'
  }

  const trialBannerIcon = { fontSize: '24px' }
  const trialBannerText = {
    flex: 1,
    fontSize: '14px',
    color: trialInfo.daysLeft <= 2 ? '#991b1b' : '#92400e'
  }
  const trialBannerButton = {
    background: trialInfo.daysLeft <= 2 ? '#dc2626' : '#f59e0b',
    color: '#fff',
    border: 'none',
    padding: '8px 20px',
    borderRadius: '8px',
    fontWeight: 'bold',
    cursor: 'pointer'
  }

  // Company Settings Styles - IMPROVED LAYOUT
  const companySettingsGrid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))',
    gap: '24px'
  }
  const companyCard = {
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    border: '1px solid #e2e8f0'
  }
  const logoSection = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '20px'
  }
  const logoPreviewStyle = {
    width: '120px',
    height: '120px',
    objectFit: 'cover',
    borderRadius: '50%',
    border: '3px solid #e2e8f0'
  }
  const logoPlaceholderStyle = {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    background: '#f1f5f9',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #cbd5e1'
  }
  const uploadButtonStyle = {
    background: '#1e3a5f',
    color: '#fff',
    padding: '8px 20px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    border: 'none'
  }
  const uploadHint = { fontSize: '12px', color: '#64748b', marginTop: '8px' }
  const formGrid = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  }
  const subscriptionInfo = { display: 'flex', flexDirection: 'column', gap: '12px' }
  const infoRow = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #f1f5f9'
  }
  const planBadge = {
    background: '#f0fdf4',
    color: '#059669',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px'
  }
  const upgradePlanBtn = {
    background: '#f59e0b',
    color: '#fff',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '12px',
    fontWeight: 'bold',
    fontSize: '14px',
    width: '100%'
  }
  const saveButtonContainer = { 
    marginTop: '24px', 
    textAlign: 'right',
    gridColumn: '1 / -1'
  }
  const saveCompanyBtn = {
    background: '#059669',
    color: '#fff',
    border: 'none',
    padding: '12px 40px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '16px'
  }
  const formGroup = { marginBottom: '16px' }
  const label = { display: 'block', marginBottom: '6px', fontWeight: '500', color: '#1e293b', fontSize: '13px' }

  // Company Settings Component - IMPROVED LAYOUT
  const CompanySettings = () => (
    <div>
      <div style={headerSubStyle}>
        <h2 style={{ fontSize: '24px', color: '#1e3a5f', margin: 0 }}>🏢 Company Settings</h2>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '8px' }}>Manage your company profile and branding</p>
      </div>

      <div style={companySettingsGrid}>
        {/* Logo Section */}
        <div style={companyCard}>
          <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 16px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
            📸 Company Logo
          </h3>
          <div style={logoSection}>
            {companyDetails.logo_url ? (
              <img src={companyDetails.logo_url} alt="Company Logo" style={logoPreviewStyle} />
            ) : (
              <div style={logoPlaceholderStyle}>
                <span style={{ fontSize: 48 }}>🏢</span>
                <p style={{ fontSize: '12px', color: '#64748b' }}>No logo uploaded</p>
              </div>
            )}
            <label style={uploadButtonStyle}>
              {uploadingLogo ? 'Uploading...' : '📸 Upload Logo'}
              <input type="file" accept="image/*" onChange={(e) => handleLogoUpload(e.target.files[0])} style={{ display: 'none' }} disabled={uploadingLogo} />
            </label>
            <p style={uploadHint}>Recommended: Square image, PNG or JPG, max 2MB</p>
          </div>
        </div>

        {/* Basic Information */}
        <div style={companyCard}>
          <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 16px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
            📋 Basic Information
          </h3>
          <div style={formGrid}>
            <div style={formGroup}>
              <label style={label}>Company Name *</label>
              <input type="text" value={companyDetails.name || ''} onChange={(e) => setCompanyDetails(prev => ({ ...prev, name: e.target.value }))} style={inputStyle} />
            </div>
            <div style={formGroup}>
              <label style={label}>Registration Number</label>
              <input type="text" value={companyDetails.registration_number || ''} onChange={(e) => setCompanyDetails(prev => ({ ...prev, registration_number: e.target.value }))} style={inputStyle} />
            </div>
            <div style={formGroup}>
              <label style={label}>Tax ID (TIN)</label>
              <input type="text" value={companyDetails.tax_id || ''} onChange={(e) => setCompanyDetails(prev => ({ ...prev, tax_id: e.target.value }))} style={inputStyle} />
            </div>
            <div style={formGroup}>
              <label style={label}>Website</label>
              <input type="url" value={companyDetails.website || ''} onChange={(e) => setCompanyDetails(prev => ({ ...prev, website: e.target.value }))} style={inputStyle} />
            </div>
            <div style={formGroup}>
              <label style={label}>Phone Number</label>
              <input type="tel" value={companyDetails.phone || ''} onChange={(e) => setCompanyDetails(prev => ({ ...prev, phone: e.target.value }))} style={inputStyle} />
            </div>
            <div style={formGroup}>
              <label style={label}>Business Type</label>
              <input type="text" value={companyDetails.business_type || ''} onChange={(e) => setCompanyDetails(prev => ({ ...prev, business_type: e.target.value }))} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div style={companyCard}>
          <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 16px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
            📍 Address Information
          </h3>
          <div style={formGrid}>
            <div style={{ ...formGroup, gridColumn: '1 / -1' }}>
              <label style={label}>Street Address</label>
              <input type="text" value={companyDetails.address || ''} onChange={(e) => setCompanyDetails(prev => ({ ...prev, address: e.target.value }))} style={inputStyle} />
            </div>
            <div style={formGroup}>
              <label style={label}>City</label>
              <input type="text" value={companyDetails.city || ''} onChange={(e) => setCompanyDetails(prev => ({ ...prev, city: e.target.value }))} style={inputStyle} />
            </div>
            <div style={formGroup}>
              <label style={label}>Country</label>
              <select value={companyDetails.country || 'Nigeria'} onChange={(e) => setCompanyDetails(prev => ({ ...prev, country: e.target.value }))} style={inputStyle}>
                <option value="Nigeria">Nigeria</option>
                <option value="Ghana">Ghana</option>
                <option value="Kenya">Kenya</option>
                <option value="South Africa">South Africa</option>
                <option value="United States">United States</option>
                <option value="United Kingdom">United Kingdom</option>
              </select>
            </div>
            <div style={formGroup}>
              <label style={label}>Number of Branches</label>
              <input type="number" value={companyDetails.number_of_branches || 1} onChange={(e) => setCompanyDetails(prev => ({ ...prev, number_of_branches: parseInt(e.target.value) }))} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* Subscription Information */}
        <div style={companyCard}>
          <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 16px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
            💳 Subscription
          </h3>
          <div style={subscriptionInfo}>
            <div style={infoRow}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>Current Plan</span>
              <strong style={planBadge}>
                {companyDetails.subscription_plan === 'free' ? '🆓 Free' : companyDetails.subscription_plan === 'professional' ? '⭐ Professional' : '👑 Enterprise'}
              </strong>
            </div>
            <div style={infoRow}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>Billing Cycle</span>
              <span style={{ fontSize: '14px', fontWeight: '500' }}>{companyDetails.billing_cycle || 'monthly'}</span>
            </div>
            <div style={infoRow}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>Status</span>
              <span style={{ color: companyDetails.payment_status === 'completed' ? '#059669' : '#f59e0b', fontSize: '14px', fontWeight: '500' }}>
                {companyDetails.payment_status === 'completed' ? '✅ Active' : '⏳ Trial'}
              </span>
            </div>
            {companyDetails.trial_ends_at && (
              <div style={infoRow}>
                <span style={{ fontSize: '14px', color: '#64748b' }}>Trial Ends</span>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>{new Date(companyDetails.trial_ends_at).toLocaleDateString()}</span>
              </div>
            )}
            <button onClick={() => navigate('/pricing')} style={upgradePlanBtn}>
              ⬆️ Upgrade Plan
            </button>
          </div>
        </div>

        {/* Save Button */}
        <div style={saveButtonContainer}>
          <button onClick={updateCompanyDetails} disabled={editingCompany} style={saveCompanyBtn}>
            {editingCompany ? 'Saving...' : '💾 Save All Changes'}
          </button>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div style={loadingStyle}>
        <div style={{ fontSize: 48 }}>🏢</div>
        <h2 style={{ fontSize: '24px', color: '#1e3a5f' }}>Loading Leadership Portal...</h2>
        <p style={{ fontSize: '14px', color: '#64748b' }}>Please wait while we load your dashboard.</p>
      </div>
    )
  }

  return (
    <PortalLayout
      session={session}
      onLogout={onLogout}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabs={tabs}
      unreadCount={unreadCount}
      portalTitle="Leadership Portal"
      portalIcon="👑"
      onShowMessages={() => setShowMessages(true)}
    >
      {/* Messages Modal */}
      {showMessages && (
        <div style={modalOverlay} onClick={() => setShowMessages(false)}>
          <div onClick={e => e.stopPropagation()}>
            <MessagingSystem 
              currentUser={session.user}
              userRole={userRole}
              onClose={() => setShowMessages(false)}
            />
          </div>
        </div>
      )}

      {/* Trial Reminder Banner - FIXED: using handleSubscribeClick */}
      {trialInfo.isTrial && trialInfo.daysLeft <= 7 && trialInfo.daysLeft > 0 && (
        <div style={trialBannerStyle}>
          <div style={trialBannerContent}>
            <span style={trialBannerIcon}>⚠️</span>
            <span style={trialBannerText}>
              <strong>Your free trial ends in {trialInfo.daysLeft} day{trialInfo.daysLeft !== 1 ? 's' : ''}!</strong>
              {trialInfo.daysLeft <= 3 && ' Subscribe now to avoid interruption.'}
            </span>
            <button 
              onClick={handleSubscribeClick} 
              style={trialBannerButton}
            >
              Subscribe Now →
            </button>
          </div>
        </div>
      )}

      {/* Header - SINGLE welcome message */}
      <div style={headerStyle}>
  <div>
    <h1 style={{ fontSize: '28px', color: '#1e3a5f', margin: 0 }}>👑 Leadership Dashboard</h1>
    <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Welcome, {session.user.email}</p>
  </div>
  <div style={buttonGroupStyle}>
    <DarkModeToggle />
    <NotificationBell 
      userId={session.user.id} 
      companyId={companyId} 
    />
    <div style={whatsappButtonWrapper}>
      <WhatsAppButton 
        type="bulk" 
        data={{ message: `Important: Leadership update from EstatesFlow` }} 
        phoneNumber="" 
        buttonText="📱 Message All" 
        session={session} 
      />
    </div>
  </div>
</div>

      {/* Company Settings Tab */}
      {activeTab === 'company' && <CompanySettings />}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <>
          <div style={responsiveStyles.statsGrid}>
            <div style={statCard}>
              <h3 style={{ fontSize: '16px', color: '#64748b' }}>👥 Staff</h3>
              <p style={statNumber}>{staff.length}</p>
            </div>
            <div style={statCard}>
              <h3 style={{ fontSize: '16px', color: '#64748b' }}>🏠 Properties</h3>
              <p style={statNumber}>{stats.totalProperties}</p>
            </div>
            <div style={statCard}>
              <h3 style={{ fontSize: '16px', color: '#64748b' }}>🎯 Leads</h3>
              <p style={statNumber}>{stats.totalLeads}</p>
            </div>
            <div style={statCard}>
              <h3 style={{ fontSize: '16px', color: '#64748b' }}>👤 Clients</h3>
              <p style={statNumber}>{stats.totalClients}</p>
            </div>
            <div style={statCard}>
              <h3 style={{ fontSize: '16px', color: '#64748b' }}>📅 Appointments</h3>
              <p style={statNumber}>{stats.totalAppointments}</p>
            </div>
            <div style={statCard}>
              <h3 style={{ fontSize: '16px', color: '#64748b' }}>📧 Pending Invites</h3>
              <p style={{ ...statNumber, color: '#f59e0b' }}>{stats.pendingInvitations}</p>
            </div>
          </div>

          <div style={revenueCard}>
            <h3 style={{ fontSize: '20px', margin: 0 }}>💰 Total Property Value</h3>
            <p style={{ fontSize: '16px', margin: '8px 0' }}>🇺🇸 USD: ${stats.totalRevenue.USD.toLocaleString()}</p>
            <p style={{ fontSize: '16px', margin: '4px 0' }}>🇪🇺 EUR: €{stats.totalRevenue.EUR.toLocaleString()}</p>
            <p style={{ fontSize: '16px', margin: '4px 0' }}>🇳🇬 NGN: ₦{stats.totalRevenue.NGN.toLocaleString()}</p>
          </div>

          <div style={responsiveStyles.twoColumnGrid}>
            <div style={recentCard}>
              <h3 style={{ fontSize: '18px', color: '#1e3a5f', margin: '0 0 16px 0' }}>📈 Top Performing Agents</h3>
              {staff.filter(s => s.role === 'agent').slice(0, 5).map(agent => {
                const agentStats = getAgentStats(agent.id)
                return (
                  <div key={agent.id} style={recentItem}>
                    <span><strong style={{ fontSize: '14px' }}>{agent.email}</strong></span>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>{agentStats.properties} props | {agentStats.leads} leads</span>
                    <div style={whatsappButtonWrapper}>
                      <WhatsAppButton type="agent" data={agent} phoneNumber="" buttonText="📱" session={session} />
                    </div>
                  </div>
                )
              })}
            </div>

            <div style={recentCard}>
              <h3 style={{ fontSize: '18px', color: '#1e3a5f', margin: '0 0 16px 0' }}>📋 Recent Activities</h3>
              {recentActivities.slice(0, 10).map(activity => (
                <div key={activity.id} style={recentItem}>
                  <span style={{ fontSize: '16px' }}>{activity.type === 'property' ? '🏠' : activity.type === 'lead' ? '🎯' : '📅'}</span>
                  <span style={{ fontSize: '14px' }}>{activity.title}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{new Date(activity.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Staff Tab */}
      {activeTab === 'staff' && (
        <StaffManagement 
          staff={staff} 
          onUpdateRole={updateStaffRole} 
          onPermissions={openPermissionsModal}
          session={session}
          onCancel={() => setActiveTab('dashboard')}
        />
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '18px', color: '#1e3a5f', margin: '0 0 8px 0' }}>📧 Invite New Staff Member</h3>
            <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '14px' }}>
              Invite team members to join your organization. They will receive an email with a link to create their account.
            </p>
            
            <form onSubmit={sendInvitation}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>Email Address *</label>
                <input type="email" placeholder="staff@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required style={inputStyle} />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>Role *</label>
                <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} style={inputStyle}>
                  {roles.map(r => (<option key={r.value} value={r.value}>{r.icon} {r.label} → {r.portal}</option>))}
                </select>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>Department *</label>
                <select value={inviteDepartment} onChange={(e) => setInviteDepartment(e.target.value)} style={inputStyle}>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <button type="submit" disabled={sendingInvite} style={addBtn}>
                {sendingInvite ? 'Sending...' : '📧 Send Invitation'}
              </button>
            </form>
          </div>

          {invitations.filter(i => i.status === 'pending').length > 0 && (
            <div style={{ ...cardStyle, marginTop: '20px' }}>
              <h3 style={{ fontSize: '18px', color: '#1e3a5f', margin: '0 0 16px 0' }}>⏳ Pending Invitations</h3>
              <div style={tableWrapper}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Email</th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Role</th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Department</th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Invited</th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Expires</th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.filter(i => i.status === 'pending').map(inv => (
                      <tr key={inv.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: 12, fontSize: '14px' }}>{inv.email}</td>
                        <td style={{ padding: 12, fontSize: '14px' }}>{inv.role}</td>
                        <td style={{ padding: 12, fontSize: '14px' }}>{inv.department || '-'}</td>
                        <td style={{ padding: 12, fontSize: '14px' }}>{new Date(inv.invited_at).toLocaleDateString()}</td>
                        <td style={{ padding: 12, fontSize: '14px', color: new Date(inv.expires_at) < new Date() ? '#dc2626' : '#64748b' }}>
                          {new Date(inv.expires_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: 12, fontSize: '14px' }}>
                          <button onClick={() => resendInvitation(inv.id)} style={{ ...editBtn, marginRight: '8px' }}>Resend</button>
                          <button onClick={() => cancelInvitation(inv.id)} style={deleteBtn}>Cancel</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Approvals Tab */}
      {activeTab === 'approvals' && (
        <div>
          <div style={headerSubStyle}>
            <h2 style={{ fontSize: '24px', color: '#1e3a5f', margin: 0 }}>✅ Pending Approvals</h2>
          </div>
          <h3 style={{ fontSize: '18px', color: '#1e3a5f' }}>💰 Expense Requests</h3>
          {expenses.length === 0 ? <p style={{ color: '#64748b', fontSize: '14px' }}>No pending expenses.</p> : (
            <div style={responsiveStyles.grid}>
              {expenses.map(e => (
                <div key={e.id} style={cardStyle}>
                  <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 8px 0' }}>{e.category}</h3>
                  <p style={{ fontSize: '14px' }}>{e.description}</p>
                  <p style={{ fontSize: 24, fontWeight: 'bold', color: '#dc2626' }}>{e.currency} {e.amount?.toLocaleString()}</p>
                  <p style={{ fontSize: '14px', color: '#64748b' }}>Requested by: {e.users?.email}</p>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <button onClick={() => approveExpense(e.id)} style={{ ...editBtn, background: '#059669' }}>✅ Approve</button>
                    <button onClick={() => rejectExpense(e.id)} style={deleteBtn}>❌ Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <h3 style={{ fontSize: '18px', color: '#1e3a5f', marginTop: 30 }}>💰 Salary Requests</h3>
          {salaryRequests.length === 0 ? <p style={{ color: '#64748b', fontSize: '14px' }}>No pending salary approvals.</p> : (
            <div style={responsiveStyles.grid}>
              {salaryRequests.map(s => {
                const agent = staff.find(a => a.id === s.agent_id)
                return (
                  <div key={s.id} style={cardStyle}>
                    <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 8px 0' }}>Salary for {agent?.email}</h3>
                    <p style={{ fontSize: '14px' }}>Month: {s.month}/{s.year}</p>
                    <p style={{ fontSize: '14px', fontWeight: 'bold' }}>Amount: {s.currency} {s.net_salary?.toLocaleString()}</p>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button onClick={() => approveSalary(s.id)} style={{ ...editBtn, background: '#059669' }}>✅ Approve</button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Properties Tab */}
      {activeTab === 'properties' && (
        <div style={responsiveStyles.grid}>
          {allProperties.map(p => (
            <div key={p.id} style={cardStyle}>
              <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 8px 0' }}>{p.title}</h3>
              <p style={{ fontSize: '14px', fontWeight: 'bold' }}>{p.currency} {p.price?.toLocaleString()}</p>
              <p style={{ fontSize: '14px', color: '#64748b' }}>📍 {p.country}, {p.city}</p>
              <p style={{ fontSize: '14px', color: '#64748b' }}>👤 Agent: {p.users?.email}</p>
              <p style={{ fontSize: '14px', color: '#64748b' }}>🛏️ {p.bedrooms} beds • 🛁 {p.bathrooms} baths</p>
            </div>
          ))}
        </div>
      )}

      {/* Leads Tab */}
      {activeTab === 'leads' && (
        <div style={responsiveStyles.grid}>
          {allLeads.map(l => (
            <div key={l.id} style={cardStyle}>
              <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 8px 0' }}>{l.client_name}</h3>
              <p style={{ fontSize: '14px' }}>📧 {l.client_email} | 📞 {l.client_phone}</p>
              <p style={{ fontSize: '14px' }}>Status: <span style={{ background: getStatusColor(l.status), padding: '4px 12px', borderRadius: 20 }}>{l.status}</span></p>
              <p style={{ fontSize: '14px' }}>Score: {l.score || 10} pts</p>
              <p style={{ fontSize: '14px', color: '#64748b' }}>👤 Agent: {l.users?.email}</p>
            </div>
          ))}
        </div>
      )}

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <div style={responsiveStyles.grid}>
          {allClients.map(c => (
            <div key={c.id} style={cardStyle}>
              <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 8px 0' }}>{c.full_name}</h3>
              <p style={{ fontSize: '14px' }}>📧 {c.email} | 📞 {c.phone}</p>
              <p style={{ fontSize: '14px', color: '#64748b' }}>👤 Agent: {c.users?.email}</p>
            </div>
          ))}
        </div>
      )}

      {/* Appointments Tab */}
      {activeTab === 'appointments' && (
        <div style={responsiveStyles.grid}>
          {allAppointments.map(a => (
            <div key={a.id} style={cardStyle}>
              <h3 style={{ fontSize: '16px', color: '#1e3a5f', margin: '0 0 8px 0' }}>📅 {new Date(a.date_time).toLocaleString()}</h3>
              <p style={{ fontSize: '14px' }}>👤 Client: {a.clients?.full_name}</p>
              <p style={{ fontSize: '14px', color: '#64748b' }}>👤 Agent: {a.users?.email}</p>
              <p style={{ fontSize: '14px' }}>Status: <span style={{ background: getStatusColor(a.status), padding: '4px 12px', borderRadius: 20 }}>{a.status}</span></p>
            </div>
          ))}
        </div>
      )}

      {/* WhatsApp Logs Tab */}
      {activeTab === 'whatsappLogs' && (
        <div>
          <h2 style={{ fontSize: '24px', color: '#1e3a5f' }}>📱 WhatsApp Message Logs</h2>
          <p style={{ fontSize: '14px', color: '#64748b' }}>Record of all WhatsApp messages sent from the CRM.</p>
          {whatsappMessages.length === 0 ? <p style={{ fontSize: '14px', color: '#64748b' }}>No messages sent yet.</p> : (
            <div style={tableWrapper}>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Date & Time</th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Sent To</th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Message</th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Type</th>
                    <th style={{ padding: 12, textAlign: 'left', fontSize: '14px' }}>Sent By</th>
                  </tr>
                </thead>
                <tbody>
                  {whatsappMessages.map(msg => (
                    <tr key={msg.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: 12, fontSize: '14px' }}>{new Date(msg.sent_at).toLocaleString()}</td>
                      <td style={{ padding: 12, fontSize: '14px' }}>{msg.to_number}</td>
                      <td style={{ padding: 12, fontSize: '14px' }}>{msg.message?.substring(0, 60)}...</td>
                      <td style={{ padding: 12, fontSize: '14px' }}><span style={{ background: '#e2e8f0', padding: '4px 12px', borderRadius: 20 }}>{msg.type || 'general'}</span></td>
                      <td style={{ padding: 12, fontSize: '14px' }}>{msg.sent_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Financials Tab */}
      {activeTab === 'financials' && (
        <div style={responsiveStyles.grid}>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '18px', color: '#1e3a5f', margin: '0 0 8px 0' }}>💰 Total Revenue</h3>
            <p style={{ fontSize: 32, fontWeight: 'bold', color: '#059669' }}>${(stats.totalRevenue.USD + stats.totalRevenue.EUR * 1.1 + stats.totalRevenue.NGN * 0.00065).toLocaleString()}</p>
            <p style={{ fontSize: '14px' }}>USD: ${stats.totalRevenue.USD.toLocaleString()}</p>
            <p style={{ fontSize: '14px' }}>EUR: €{stats.totalRevenue.EUR.toLocaleString()}</p>
            <p style={{ fontSize: '14px' }}>NGN: ₦{stats.totalRevenue.NGN.toLocaleString()}</p>
          </div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: '18px', color: '#1e3a5f', margin: '0 0 8px 0' }}>📈 Business Health</h3>
            <p style={{ fontSize: '14px' }}>Total Properties: {stats.totalProperties}</p>
            <p style={{ fontSize: '14px' }}>Total Leads: {stats.totalLeads}</p>
            <p style={{ fontSize: '14px' }}>Conversion Rate: {stats.totalLeads > 0 ? ((stats.totalClients / stats.totalLeads) * 100).toFixed(1) : 0}%</p>
            <p style={{ fontSize: '14px' }}>Active Staff: {staff.filter(s => s.role).length}</p>
          </div>
        </div>
      )}

      {/* Permission Modal */}
      {showPermissionModal && selectedStaff && (
        <div style={modalOverlay} onClick={() => setShowPermissionModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: '20px', color: '#1e3a5f', margin: '0 0 16px 0' }}>Permissions for {selectedStaff.email}</h3>
            <div style={{ marginBottom: 20, padding: 15, background: '#f8fafc', borderRadius: 12 }}>
              <h4 style={{ fontSize: '16px', margin: '0 0 12px 0' }}>💰 Expense Permissions</h4>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: '14px' }}>
                <input type="checkbox" checked={permissions.expenses?.view} onChange={e => setPermissions({ ...permissions, expenses: { ...permissions.expenses, view: e.target.checked } })} />
                View Expenses
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: '14px' }}>
                <input type="checkbox" checked={permissions.expenses?.create} onChange={e => setPermissions({ ...permissions, expenses: { ...permissions.expenses, create: e.target.checked } })} />
                Create Expenses
              </label>
            </div>
            <div style={{ marginBottom: 20, padding: 15, background: '#f8fafc', borderRadius: 12 }}>
              <h4 style={{ fontSize: '16px', margin: '0 0 12px 0' }}>💰 Salary Permissions</h4>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: '14px' }}>
                <input type="checkbox" checked={permissions.salaries?.view} onChange={e => setPermissions({ ...permissions, salaries: { ...permissions.salaries, view: e.target.checked } })} />
                View Salaries
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={updatePermissions} style={{ ...editBtn, background: '#059669' }}>Save</button>
              <button onClick={() => setShowPermissionModal(false)} style={cancelBtn}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  )
}

export default LeadershipPortal